import type { Editor } from "@tiptap/core";
import type { Node } from '@tiptap/pm/model';
import { videoWidth, videoFrameStyle, videoBlockStyle } from '../../general/youtube-video';

export class VideoView {
  node: Node;
  editor: Editor;
  getPos: () => number;
  dom: HTMLElement;
  contentDOM?: HTMLElement;
  iframe: HTMLIFrameElement;
  wrapper: HTMLDivElement;

  constructor(node: any, editor: any, getPos: any, HTMLAttributes: any) {
    this.node = node;
    this.editor = editor;
    this.getPos = getPos;

    // 1. Create the outer container (Figure or Div)
    const doc = editor.options.element?.ownerDocument || document;
    this.dom = doc.createElement("div");
    
    // 2. Create the internal structure
    this.wrapper = doc.createElement("div");
    this.wrapper.className = "video-wrapper";
    
    this.iframe = doc.createElement("iframe");
    this.iframe.title = 'YouTube video';
    this.iframe.setAttribute("frameborder", "0");
    this.iframe.setAttribute("allowfullscreen", "true");
    this.iframe.setAttribute("allow", "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture");

    this.wrapper.appendChild(this.iframe);
    this.dom.appendChild(this.wrapper);

    this.render();
  }

  render() {
    const { videoId, align } = (this.node as any).attrs;
    const selected = this.editor.state.selection.from === this.getPos();

    // Sync Iframe Source
    const newSrc = `https://www.youtube.com/embed/${videoId}`;
    if (this.iframe.src !== newSrc) {
      this.iframe.src = newSrc;
    }

    // FIX: Single className assignment (same as Image logic)
    const alignmentClass = `align-${align || 'center'}`;
    const selectionClass = selected ? "ProseMirror-selectednode" : "";
    this.dom.className = `video-node video-block ${alignmentClass} ${selectionClass}`;
    this.dom.style.cssText = videoBlockStyle(align);
    this.wrapper.style.cssText = videoFrameStyle((this.node as any).attrs.width);
    this.wrapper.style.height = 'auto';
    this.wrapper.style.aspectRatio = '';
    this.iframe.setAttribute('style', videoFrameStyle('100%'));

    // Manage Toolbar
    this.renderToolbar(selected);
  }

  renderToolbar(selected: boolean) {
    this.dom.querySelector(".video-toolbar")?.remove();
    if (!selected) return;

    const doc = this.dom.ownerDocument;
    const toolbar = doc.createElement("div");
    toolbar.className = "video-toolbar";
    toolbar.setAttribute('role', 'toolbar');
    toolbar.setAttribute('aria-label', 'Video controls');
    toolbar.onmousedown = event => {
      if ((event.target as HTMLElement).closest('button')) event.preventDefault();
      event.stopPropagation();
    };

    ["left", "center", "right"].forEach((dir) => {
      const btn = doc.createElement("button");
      btn.type = 'button';
      btn.innerText = dir.charAt(0).toUpperCase() + dir.slice(1);
      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.editor.chain().focus().updateAttributes("video", { align: dir }).run();
      };
      toolbar.appendChild(btn);
    });

    const label = doc.createElement('label');
    label.textContent = 'Width (px) ';
    const input = doc.createElement('input');
    input.type = 'number'; input.min = '200'; input.max = '3840'; input.step = '1';
    input.setAttribute('aria-label', 'Video width in pixels');
    const width = videoWidth((this.node as any).attrs.width);
    input.value = width === '100%' ? '' : String(width);
    input.placeholder = 'Full width';
    const updateWidth = (value: number | '100%') => {
      const pos = this.getPos();
      const current = this.editor.state.doc.nodeAt(pos);
      if (!current || current.type !== this.node.type) return;
      this.editor.view.dispatch(this.editor.state.tr.setNodeMarkup(pos, undefined, { ...current.attrs, width: value }));
    };
    const commitWidth = () => {
      if (input.value && input.checkValidity()) updateWidth(videoWidth(input.value));
      else input.reportValidity();
    };
    input.onchange = commitWidth;
    input.onkeydown = event => {
      if (event.key === 'Enter') { event.preventDefault(); commitWidth(); }
      event.stopPropagation();
    };
    label.appendChild(input); toolbar.appendChild(label);
    const fullWidth = doc.createElement('button');
    fullWidth.type = 'button'; fullWidth.textContent = 'Full width';
    fullWidth.onclick = event => { event.preventDefault(); event.stopPropagation(); updateWidth('100%'); };
    toolbar.appendChild(fullWidth);

    const delBtn = doc.createElement("button");
    delBtn.type = 'button';
    delBtn.innerText = "Delete";
    delBtn.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.editor.chain().focus().deleteSelection().run();
    };
    toolbar.appendChild(delBtn);

    this.dom.prepend(toolbar);
  }

  stopEvent(event: Event) {
    return this.dom.querySelector('.video-toolbar')?.contains(event.target as globalThis.Node) ?? false;
  }

  // Tiptap calls this when the node is updated in the document
  update(node: any) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  selectNode() {
    this.render();
  }

  deselectNode() {
    this.render();
  }
}
